import { isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { VoiceVisualizer } from "react-voice-visualizer";

import SessionHeader from "features/Session/SessionHeader";
import SessionStopDialog from "features/Session/SessionStopDialog";
import SessionTranscriptionDialog from "features/Session/SessionTranscriptionDialog";

import { cn } from "utils/cn";

import Button from "components/Button";
import Container from "components/Container";
import ProgressStatus from "components/ProgressStatus";

import RecorderPointIndicator from "./RecorderPointIndicator";
import { useVoiceRecorder } from "./useVoiceRecorder";
import VoiceRecorderButton from "./VoiceRecorderButton";

export default function VoiceRecorder() {
  const { t } = useTranslation();

  const {
    isPending,
    open,
    setOpen,
    recordingState,
    handleButtonClick,
    handleStopRecording,
    handleTranscribeRecording,
    getVisualizerHeight,
    pauseOpen,
    setPauseOpen,
    recorderControls,
  } = useVoiceRecorder();

  if (isPending) {
    return <SessionTranscriptionDialog open={open} setOpen={setOpen} />;
  }

  return (
    <>
      <SessionHeader />
      <div className="flex flex-col justify-between h-full md:pt-16 overflow-auto">
        <ProgressStatus />
        <Container
          className={cn(
            "!pb-2 md:!pb-0 transition-colors duration-300 md:border-1 md:rounded-md border-general-gray-400 border-b pt-10 md:pt-0 mb-32",
            recordingState === "recording" ? "bg-general-green-500" : "bg-general-gray-200",
          )}
        >
          <div className="flex flex-col items-center md:p-5">
            <div className="relative flex items-center w-full mb-5 p-4 md:p-0">
              {recordingState !== "stopped" && <RecorderPointIndicator />}
              <VoiceVisualizer
                controls={recorderControls}
                isDefaultUIShown={false}
                isControlPanelShown={false}
                mainContainerClassName={cn(
                  recordingState === "recording" ? "bg-general-green-600" : "bg-general-gray-300",
                  "p-4 rounded w-full transition-colors duration-300",
                )}
                mainBarColor="#8A929E"
                secondaryBarColor="#8A929E"
                barWidth={isMobile ? 4 : 2}
                speed={isMobile ? 1 : 4}
                height={getVisualizerHeight()}
              />
            </div>

            <p className="font-semibold text-4xl md:text-2xl">{recorderControls.formattedRecordingTime}</p>
          </div>
          <div className="relative h-24">
            <VoiceRecorderButton recordingState={recordingState} handleButtonClick={handleButtonClick} />
          </div>
          <div className="md:h-40 w-full bg-white rounded-b-md" />
        </Container>
        <div className="flex gap-4 md:justify-end p-4 md:fixed bottom-0 left-0 right-0 md:bg-general-gray-200 md:border-t border-general-gray-400">
          <SessionStopDialog stopRecording={handleStopRecording} open={pauseOpen} setOpen={setPauseOpen} />
          <Button
            type="button"
            className="flex-1 md:flex-none md:w-60"
            variant="secondary"
            onClick={() => setPauseOpen(true)}
          >
            {t("button.pauseSession")}
          </Button>
          <Button
            disabled={recordingState === "idle" || recordingState === "recording"}
            className="flex-1 md:flex-none md:w-60"
            variant="primary"
            onClick={handleTranscribeRecording}
          >
            {t("button.reviewFinding")}
          </Button>
        </div>
      </div>
    </>
  );
}
